import type {
  CreateContractImportJobsMutationOptions,
  CreateContractImportJobsPayload,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysContractImportJob } from '../queryKeys';

import { createContractImportJobs } from './createContractImportJobs';

/**
 * Custom hook for bulk-creating contract import jobs from FileHub file IDs.
 * Each valid file starts one parsing job (max 10 files per request).
 *
 * @param options - Additional options for the mutation.
 * @returns An object containing the mutation function and related properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Contract-Import-Jobs/operation/create_import_jobs_contracts_import_jobs_post
 */
export const useMutationCreateContractImportJobs = (
  options?: CreateContractImportJobsMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateContractImportJobsPayload) =>
      createContractImportJobs(payload),
    ...options,
    onSuccess(data, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysContractImportJob.getAll(),
      });

      return options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
